(ns story-builder.util.math
  (:require
   [cljs.math]))

(defn gcd
  "Greatest Common Divisor (Euclidean algorithm),"
  [a b & others]
  (when (and a b)
    (cond
      (and (zero? b) (nil? others)) (js/Math.abs a)
      (zero? b) (recur a (first others) (next others))
      :else (recur b (rem a b) others))))

(defn reduce-fraction
  "Reduces the fraction to the simplest equivalent."
  [numerator denominator]
  (if-let [gcd (gcd numerator denominator)]
    [(/ numerator gcd) (/ denominator gcd)]
    [numerator denominator]))

(defn normalize-angle
  "Normalizes an angle to be within the range of 0-180 degrees.
   If the angle is negative, it is translated to a positive angle between 0 and 180.
   The input angle should be in degrees."
  [deg]
  (let [abs-deg (js/Math.abs deg)]
    (cond
      (< abs-deg 90)    abs-deg
      (< abs-deg 180)   (- 180 abs-deg)
      (< abs-deg 270)   (- abs-deg 180)
      :else             (- abs-deg 360))))

(defn unrotate-rect
  "Returns the original dimensions of a rotated rectangle.
   Doesn't work for 45deg rotations when the solution is ambiguous."
  [{:keys [rotation] :as rect}]
  (let [rotation (normalize-angle rotation)]
    (if (#{0 180} rotation)
      rect
      (let [rads        (cljs.math/to-radians rotation)
            left        (:left rect)
            right       (:right rect)
            top         (:top rect)
            bottom      (:bottom rect)
            width       (- right left)
            height      (- bottom top)
            cos         (cljs.math/cos rads)
            sin         (cljs.math/sin rads)
            cos-sq      (cljs.math/pow cos 2)
            sin-sq      (cljs.math/pow sin 2)
            orig-width  (/ (- (* width cos) (* height sin))
                           (abs (- cos-sq sin-sq)))
            orig-height (/ (- (* height cos) (* width sin))
                           (abs (- sin-sq cos-sq)))
            origin-x    (+ left (/ width 2))
            origin-y    (+ top (/ height 2))
            orig-left   (- origin-x (/ orig-width 2))
            orig-right  (+ origin-x (/ orig-width 2))
            orig-top    (- origin-y (/ orig-height 2))
            orig-bottom (+ origin-y (/ orig-height 2))
            rect        {:left     orig-left
                         :right    orig-right
                         :top      orig-top
                         :bottom   orig-bottom
                         :width    orig-width
                         :height   orig-height
                         :rotation 0}]
        rect))))

(defn unrotated-dimensions
  [[bounding-width bounding-height :as bounding-dimensions] deg]
  (let [deg (normalize-angle deg)]
    (cond
      (#{0 180 360} deg)
      bounding-dimensions
      (#{90 270} deg)
      [bounding-height bounding-width]
      (#{45 135 225} deg)
      (let [ratio (/ bounding-width bounding-height)]
        [(* (/ (* (js/Math.sqrt 2) ratio)
               (+ ratio 1))
            bounding-width)
         (* (/ (js/Math.sqrt 2)
               (+ ratio 1))
            bounding-width)])
      :else
      (let [rad   (cljs.math/to-radians deg)
            cos   (cljs.math/cos rad)
            sin   (cljs.math/sin rad)
            denom (- cos (/ (js/Math.pow sin 2) cos))]
        [(/ (- bounding-width (* (/ sin cos) bounding-height)) denom)
         (/ (- bounding-height (* (/ sin cos) bounding-width)) denom)]))))

(defn bounding-box-dimensions
  [[width height] deg]
  (let [rad (cljs.math/to-radians (normalize-angle deg))
        cos (js/Math.abs (cljs.math/cos rad))
        sin (js/Math.abs (cljs.math/sin rad))]
    [(when width (+ (* width cos) (* height sin)))
     (when height (+ (* height cos) (* width sin)))]))

(defn rotate
  [[x y] deg]
  (let [rad (cljs.math/to-radians deg)
        cos (cljs.math/cos rad)
        sin (cljs.math/sin rad)]
    [(- (* x cos)
        (* y sin))
     (+ (* x sin)
        (* y cos))]))

(defn rotate-around
  "Returns a rotated point around an origin by some specified degrees."
  [point origin deg]
  (let [[xp yp] point
        [xc yc] origin
        [xp yp] (rotate [(- xp xc) (- yp yc)] deg)]
    [(+ xp xc) (+ yp yc)]))

(defn midpoint
  [point1 point2]
  [(/ (+ (first point1) (first point2)) 2)
   (/ (+ (second point1) (second point2)) 2)])

(defn distance-between
  [[x y] [x1 y1]]
  (let [dx (- x1 x)
        dy (- y1 y)]
    (Math/sqrt (+ (* dx dx) (* dy dy)))))

(defn angle-between
  [point1 point2]
  (let [dx (- (first point2) (first point1))
        dy (- (second point2) (second point1))
        rad (Math/atan2 dy dx)]
    (cljs.math/to-degrees rad)))

(defn rect->center
  [{:keys [left top width height]}]
  (let [center-x (+ left (/ width 2))
        center-y (+ top (/ height 2))]
    [center-x center-y]))

(defn rect->corners [rect]
  (let [top     (:top rect)
        bottom  (:bottom rect)
        right   (:right rect)
        left    (:left rect)
        corners [[left top] [right top] [right bottom] [left bottom]]]
    corners))

(defn corners->bounding-rect
  "Compute bounding box from list of corner points."
  [corners]
  (let [xs (map first corners)
        ys (map second corners)]
    {:left    (apply min xs)
     :top     (apply min ys)
     :right   (apply max xs)
     :bottom  (apply max ys)
     :width   (- (apply max xs) (apply min xs))
     :height  (- (apply max ys) (apply min ys))}))

(defn translate-corners [[corners [origin-x origin-y]] f [x y]]
  (let [to-x    (when x (apply f (map first corners)))
        to-y    (when y (apply f (map second corners)))
        delta-x (when x (- x to-x))
        delta-y (when y (- y to-y))]
    [(mapv (fn [[x y]] [(+ x delta-x) (+ y delta-y)]) corners)
     [(+ origin-x delta-x) (+ origin-y delta-y)]]))

(defn get-axes
  [points]
  (mapv (fn [[p1 p2]]
          (let [[x1 y1] p1
                [x2 y2] p2
                dx (- x2 x1)
                dy (- y2 y1)]
            [dy (- dx)])) (partition 2 1 (conj points (first points)))))

(defn project
  [points axis]
  (let [projections (mapv (fn [point]
                            (let [[x y] point
                                  [ax ay] axis]
                              (+ (* x ax) (* y ay)))) points)]
    [(apply min projections) (apply max projections)]))

(defn overlap?
  [projection1 projection2]
  (let [[min1 max1] projection1
        [min2 max2] projection2]
    (and (<= min1 max2) (<= min2 max1))))

(defn intersect?
  "Checks if two rectangles intersect"
  [r1 r2]
  (let [axes (concat (get-axes r1) (get-axes r2))]
    (every? (fn [axis]
              (let [projection1 (project r1 axis)
                    projection2 (project r2 axis)]
                (overlap? projection1 projection2))) axes)))

(defn angle->vector
  "Converts an angle in degrees to a normalized 2D vector"
  [angle]
  (let [radians (cljs.math/to-radians angle)
        x       (cljs.math/cos radians)
        y       (- (Math/sin radians))] ;; Negative because y is down.
    [x y]))

(defn binary-search-move
  "Moves r1 in the direction of its rotation until it no longer intersects with r2"
  [r1 r2 rotation]
  (let [move-vector  (angle->vector rotation)
        move         (fn [r [vx vy] distance] (mapv (fn [[px py]] [(+ px (* distance vx)) (+ py (* distance vy))]) r))
        max-distance 1000
        tolerance    1] ;; Add a tolerance to avoid infinite loop
    (loop [min 0
           max max-distance]
      (let [mid         (/ (+ min max) 2)
            r1-moved    (move r1 move-vector mid)
            intersects? (intersect? r1-moved r2)]
        (cond
          (< (- max min) tolerance) (+ 2 mid) ; Add two pixels just to make sure.
          (and intersects? (< min mid)) (recur mid max)
          :else (recur min mid))))))

(defn area-corners
  "Calculate the area of a rectangle given its corners."
  [[[x1 y1] [x2 _] [_ y3]]]
  (* (- x2 x1) (- y3 y1)))

(defn intersect-area-corners
  "Calculate the area of intersection of two rectangles given their corners."
  [rect1 rect2]
  (let [x1 (max (first (first rect1)) (first (first rect2)))
        y1 (max (second (first rect1)) (second (first rect2)))
        x2 (min (first (second rect1)) (first (second rect2)))
        y2 (min (second (nth rect1 3)) (second (nth rect2 3)))]
    (if (or (>= x1 x2) (>= y1 y2))
      0
      (* (- x2 x1) (- y2 y1)))))

(defn percentage-outside-corners
  "Calculate the percentage of the second rectangle outside the first rectangle."
  [rect1 rect2]
  (let [area2        (area-corners rect2)
        intersection (intersect-area-corners rect1 rect2)
        outside-area (- area2 intersection)]
    (* 100 (/ outside-area area2))))